import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { PaperPlaneTilt, FileText, MagnifyingGlass, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

export function EmailLogSection() {
  const [data, setData] = useState({ records: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [viewLog, setViewLog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.getEmailLog({ page: 1, limit: 200, search: search || undefined }); setData(res.data); }
    catch { toast.error('Errore caricamento log'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(() => load(), 300); return () => clearTimeout(t); }, [load]);

  const getTargetLabel = (l) => {
    const type = l.target_type;
    if (type === 'insegnante') return { name: l.teacher_name || '-', badge: 'bg-blue-100 text-blue-700', label: 'Insegnante' };
    if (type === 'potenziali') return { name: l.student_name || '-', badge: 'bg-purple-100 text-purple-700', label: 'Potenziale' };
    return { name: l.student_name || '-', badge: 'bg-green-100 text-green-700', label: 'Alunno' };
  };

  const renderBody = (body) => {
    if (!body) return <p className="text-[#A1A1AA] italic">Nessun corpo email salvato</p>;
    // Heuristic: se contiene tag HTML, renderizza come HTML; altrimenti come testo
    const looksHtml = /<\w+[^>]*>/.test(body);
    if (looksHtml) {
      return <div className="prose prose-sm max-w-none [&_p]:my-1" dangerouslySetInnerHTML={{ __html: body }} />;
    }
    return <pre className="whitespace-pre-wrap font-sans text-sm text-[#27272A]">{body}</pre>;
  };

  return (
    <>
      <div className="mb-6 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Log Invii Email</h1>
        <p className="text-[#52525B] text-sm">Storico di tutte le email inviate</p>
      </div>
      <div className="flex items-center gap-3 mb-4 animate-fade-in animate-delay-1">
        <div className="relative w-full sm:w-[300px]">
          <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <Input type="text" placeholder="Cerca per nome, email, oggetto..." value={search} onChange={e => setSearch(e.target.value)} className="rounded-sm h-9 text-sm pl-8" data-testid="email-log-search" />
        </div>
        <div className="text-sm text-[#52525B] font-medium ml-auto" data-testid="email-log-count">{data.total} invii</div>
      </div>
      <div className="animate-fade-in animate-delay-2 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        {loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : data.records.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm"><PaperPlaneTilt size={48} className="mx-auto text-[#E4E4E7] mb-4" /><p className="text-[#52525B]">{search ? 'Nessun risultato' : 'Nessuna email inviata'}</p></div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full" data-testid="email-log-table">
              <thead><tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Data/Ora</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Tipo</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Destinatario</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Insegnante</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Email</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Oggetto</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Modello</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">E.C.</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Inviato da</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Vedi</th>
              </tr></thead>
              <tbody>{data.records.map(l => {
                const ts = new Date(l.sent_at);
                const target = getTargetLabel(l);
                let teacherDisplay = '-';
                if (target.label === 'Insegnante') teacherDisplay = l.teacher_name || '-';
                else if (l.teachers_of_student && l.teachers_of_student.length) teacherDisplay = l.teachers_of_student.join(', ');
                return (<tr key={l.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap"><div>{ts.toLocaleDateString('it-IT')}</div><div className="text-[#A1A1AA]">{ts.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</div></td>
                  <td className="px-3 py-1.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${target.badge}`}>{target.label}</span></td>
                  <td className="px-3 py-1.5 text-sm font-medium uppercase">{target.name}</td>
                  <td className="px-3 py-1.5 text-xs uppercase text-[#52525B]">{teacherDisplay}</td>
                  <td className="px-3 py-1.5 text-xs text-[#52525B]">{l.recipients?.join(', ')}</td>
                  <td className="px-3 py-1.5 text-sm">{l.subject}</td>
                  <td className="px-3 py-1.5 text-xs"><span className={`px-2 py-0.5 rounded ${l.template_name === 'Mail libera' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>{l.template_name}</span></td>
                  <td className="px-3 py-1.5 text-center">{l.has_estratto_conto ? <FileText size={16} className="inline text-amber-600" /> : '-'}</td>
                  <td className="px-3 py-1.5 text-sm text-[#52525B]">{l.sent_by}</td>
                  <td className="px-3 py-1.5 text-center">
                    <button
                      onClick={() => setViewLog(l)}
                      className="p-1.5 rounded hover:bg-[#F4F4F5] text-[#52525B] hover:text-[#2D2A4A] transition-colors"
                      title="Visualizza testo email"
                      data-testid={`view-email-${l.id}`}
                    >
                      <Eye size={16} weight="bold" />
                    </button>
                  </td>
                </tr>);
              })}</tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!viewLog} onOpenChange={(o) => !o && setViewLog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Email inviata</DialogTitle>
          </DialogHeader>
          {viewLog && (
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="font-semibold text-[#52525B]">Data:</span> {new Date(viewLog.sent_at).toLocaleString('it-IT')}</div>
                <div><span className="font-semibold text-[#52525B]">Inviato da:</span> {viewLog.sent_by || '-'}</div>
                <div className="col-span-2"><span className="font-semibold text-[#52525B]">A:</span> {(viewLog.recipients || []).join(', ')}</div>
                <div className="col-span-2"><span className="font-semibold text-[#52525B]">Modello:</span> {viewLog.template_name || '-'}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B] mb-1">Oggetto</div>
                <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-3 text-sm font-medium">{viewLog.subject}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B] mb-1">Corpo email</div>
                <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 max-h-[50vh] overflow-y-auto">
                  {renderBody(viewLog.body)}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
